import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel, HubConnectionState } from "@microsoft/signalr";
import { Result } from '../models/result';
import { Subject } from 'rxjs';
import { BidPublishingData } from '../models/bidPublishingData';
import { AuctionStatusPublishingData } from '../models/auctionStatusPublishingData';
import { AppConfig } from '../app.config';
import { AuthenticationService } from './AuthenticationService';
import { State } from '../models/state';
import { County } from '../models/county';
import { LegalAdPublishingData } from '../models/legalAdPublishingData';
import { EmailTemplateDTO } from '../DTO/emailTemplateDTO';
import { PropertyViewsPublishingData } from '../models/propertyViewsPublishingData';
import { PropertyPublishingData } from '../models/propertyPublishingData';
import { PrivateAuctionAuthorizationPublishingData } from '../models/privateAuctionAuthorizationPublishingData';
import { PropertyLockPublishingData } from '../models/propertyLockPublishingData';
import { LegalAdRuleDTO } from '../DTO/legalAdRuleDTO';
import { AuctionRuleDTO } from '../DTO/auctionRuleDTO';
import { LegalInformationRuleDTO } from '../DTO/legalInformationRuleDTO';
import { DocumentTemplateDTO } from '../DTO/documentTemplateDTO';
import { AppService } from '../app.service';
import { LoggingService } from './LoggingService';
import { JudgeDTO } from '../DTO/judgeDTO';
import { LinkDTO } from '../DTO/linkDTO';
import { FolderStructureTemplateDTO } from '../DTO/folderStructureTemplateDTO';
import { LockAccountPublishingData } from '../models/lockAccountPublishingData';
import { PortfolioPublishingData } from '../models/portfolioPublishingData';
import { ClientPublishingData } from '../models/clientPublishingData';
import { VendorPublishingData } from '../models/vendorPublishingData';
import { LawFirmPublishingData } from '../models/lawFirmPublishingData';
import { PropertyPhasePublishingData } from '../models/propertyPhasePublishingData';

@Injectable()  
export class SignalRService {

    private reconnectTimer?: ReturnType<typeof setTimeout>;
    private subscriberHubConnection?: HubConnection;
    
    public connectionReconnectedSubject = new Subject();
    public newHubMessageReceivedSubject = new Subject<{type:string, obj:any}>();

    constructor(private appConfig:AppConfig, private authService:AuthenticationService, private logger:LoggingService) {
        // Constructor initialization
        this.logger.log("Creating SignalR Service");
        this.authService.authorizedSubject.subscribe(authorized=> {
            if(authorized && !this.subscriberHubConnection) this.startConnection();
        });
    }

    get businessId():string{
        return this.appConfig.businessId;
    }
    get token(){
        return this.appConfig.accessToken;
    }
    get serviceURL():string {
        return this.appConfig.getConfig("clientconnectivityservice_external_url") + '/subscriberHub';
    }
    get connectionId():string|null{
        return this.subscriberHubConnection?.connectionId ?? null;
    }

    public async authenticateSubscriber(): Promise<void> {
        const connection = this.subscriberHubConnection;
        if (!connection) {
            this.logger.log("SignalR authentication deferred - no hub connection");
            return;
        }
        if (connection.state !== HubConnectionState.Connected) {
            this.logger.log("SignalR authentication deferred - connection state=" + connection.state);
            return;
        }
        const au = this.authService.currentUserValue;
        this.logger.log("SignalR authenticating subscriber. ConnectionId=" + connection.connectionId);
        try {
            const result: Result = await connection.invoke(
                "Subscribe",
                au?.id,
                this.businessId,
                au?.role
            );
            this.logger.log("Subscription authentication: success=" + result.success + ", message=" + result.message);
        }
        catch (error) {
            this.logger.log("SignalR subscriber authentication failed", error);
        }
    }

    private async startConnection(): Promise<void> {
        this.logger.log("SignalR connection starting");

        const oldConnection = this.subscriberHubConnection;

        if (oldConnection) {
            this.subscriberHubConnection = undefined;

            try {
                await oldConnection.stop();
            }
            catch (error) {
                this.logger.log("Error stopping previous SignalR connection", error);
            }
        }

        const logLevel = this.appConfig.isDev ? LogLevel.Debug : LogLevel.None;

        const connection = new HubConnectionBuilder()
            .withUrl(this.serviceURL)
            .configureLogging(logLevel)
            .withAutomaticReconnect()
            .build();

        this.subscriberHubConnection = connection;

        connection.onreconnecting((error?: Error) => {
            this.connectionReconnecting(error);
        });

        connection.onreconnected(() => {
            this.connectionReconnected();
        });

        connection.onclose((error?: Error) => {
            this.connectionClose(connection, error);
        });

        this.registerSubscriberHubMethods();

        try {
            await connection.start();
            await this.connectionStart();
        }
        catch (reason) {
            this.hubConnectionStartError(reason);
        }
    }

    private registerSubscriberHubMethods(){
        if(!this.subscriberHubConnection) return;
        this.subscriberHubConnection.on("TimeSync", (time:string) => {
            let d= new Date(time);
            AppService.timeSync(d);
        });
        this.subscriberHubConnection.on("PropertyUpdate", (pdp:PropertyPublishingData) => {
            this.logger.log("Hub message: PropertyUpdate for "+ pdp.propertyId);
            this.newHubMessageReceivedSubject.next({type:"PropertyUpdate",obj:pdp});
        });
        this.subscriberHubConnection.on("PropertyLock", (plpd:PropertyLockPublishingData) => {
            this.logger.log("Hub message: PropertyLock for "+ plpd.propertyId);
            this.newHubMessageReceivedSubject.next({type:"PropertyLock",obj:plpd});
        });
         this.subscriberHubConnection.on("PropertyPhase", (pppd:PropertyPhasePublishingData) => {
            this.logger.log("Hub message: PropertyPhase for "+ pppd.propertyId);
            this.newHubMessageReceivedSubject.next({type:"PropertyPhase",obj:pppd});
        });
        this.subscriberHubConnection.on("PropertyDelete", (pdp:PropertyPublishingData) => {
            this.logger.log("Hub message: PropertyDelete for "+ pdp.propertyId);
            this.newHubMessageReceivedSubject.next({type:"PropertyDelete",obj:pdp});
        });
        this.subscriberHubConnection.on("PropertyViewed", (pvpd: PropertyViewsPublishingData) => {
            this.logger.log("Hub message: PropertyViewed for "+ pvpd.propertyId);
            this.newHubMessageReceivedSubject.next({type:"PropertyViewed",obj:pvpd});
        });
        this.subscriberHubConnection.on("InventoryUpdate", (inventoryIds:number[]) => {
            this.logger.log("Hub message: InventoryUpdate for "+ inventoryIds.toString());
            this.newHubMessageReceivedSubject.next({type:"InventoryUpdate",obj:inventoryIds});
        });
        this.subscriberHubConnection.on("InventoryDelete", (inventoryIds:number[]) => {
            this.logger.log("Hub message: PropertyDelete for "+ inventoryIds.toString());
            this.newHubMessageReceivedSubject.next({type:"InventoryDelete",obj:inventoryIds});
        });
        this.subscriberHubConnection.on("Bid", (bpd:BidPublishingData) => {
            this.logger.log("Hub message: Bid for "+ bpd.propertyId);
            this.newHubMessageReceivedSubject.next({type:"Bid",obj:bpd});
        });
        this.subscriberHubConnection.on("BidEqualized", (bpd:BidPublishingData) => {
            this.logger.log("Hub message: Bid equalized for "+ bpd.propertyId);
            this.newHubMessageReceivedSubject.next({type:"BidEqualized",obj:bpd});
        });
        this.subscriberHubConnection.on("AuctionStatus", (spd:AuctionStatusPublishingData) => {
            this.logger.log("Hub message: Auction status for "+ spd.propertyId);
            this.newHubMessageReceivedSubject.next({type:"AuctionStatus",obj:spd});
        });
        this.subscriberHubConnection.on("AuctionStarted", (spd:AuctionStatusPublishingData) => {
            this.logger.log("Hub message: Auction started for "+ spd.propertyId);
            this.newHubMessageReceivedSubject.next({type:"AuctionStarted",obj:spd});
        });
        this.subscriberHubConnection.on("AuctionEnded", (spd:AuctionStatusPublishingData) => {
            this.logger.log("Hub message: Auction ended for "+ spd.propertyId);
            this.newHubMessageReceivedSubject.next({type:"AuctionEnded",obj:spd});
        });
        this.subscriberHubConnection.on("AuctionCancelled", (spd:AuctionStatusPublishingData) => {
            this.logger.log("Hub message: Auction cancelled for "+ spd.propertyId);
            this.newHubMessageReceivedSubject.next({type:"AuctionCancelled",obj:spd});
        });
        this.subscriberHubConnection.on("AuctionEndPostponed", (spd:AuctionStatusPublishingData) => {
            this.logger.log("Hub message: Auction end postponed for "+ spd.propertyId);
            this.newHubMessageReceivedSubject.next({type:"AuctionEndPostponed",obj:spd});
        });
        this.subscriberHubConnection.on("State", (s:State) => {
            this.logger.log("Hub message: State update: "+ s.stateCode);
            this.newHubMessageReceivedSubject.next({type:"State",obj:s});
        });
        this.subscriberHubConnection.on("County", (c:County) => {
            this.logger.log("Hub message: County update: "+ c.name);
            this.newHubMessageReceivedSubject.next({type:"County",obj:c});
        });
        this.subscriberHubConnection.on("LegalAd", (lapd:LegalAdPublishingData) => {
            this.logger.log("Hub message: LegalAd: "+ lapd.id);
            this.newHubMessageReceivedSubject.next({type:"LegalAd",obj:lapd});
        });
        this.subscriberHubConnection.on("LegalAdRule", (lar:LegalAdRuleDTO) => {
            this.logger.log("Hub message: LegalAdRule: "+ lar.id);
            this.newHubMessageReceivedSubject.next({type:"LegalAdRule",obj:lar});
        });
        this.subscriberHubConnection.on("DeleteLegalAdRule", (lar:LegalAdRuleDTO) => {
            this.logger.log("Hub message: DeleteLegalAdRule: "+ lar.id);
            this.newHubMessageReceivedSubject.next({type:"DeleteLegalAdRule",obj:lar});
        });
        this.subscriberHubConnection.on("AuctionRule", (ar:AuctionRuleDTO) => {
            this.logger.log("Hub message: AuctionRule: "+ ar.id);
            this.newHubMessageReceivedSubject.next({type:"AuctionRule",obj:ar});
        });
        this.subscriberHubConnection.on("DeleteAuctionRule", (ar:AuctionRuleDTO) => {
            this.logger.log("Hub message: DeleteAuctionRule: "+ ar.id);
            this.newHubMessageReceivedSubject.next({type:"DeleteAuctionRule",obj:ar});
        });
        this.subscriberHubConnection.on("LegalInformationRule", (lir:LegalInformationRuleDTO) => {
            this.logger.log("Hub message: LegalInformationRule: "+ lir.id);
            this.newHubMessageReceivedSubject.next({type:"LegalInformationRule",obj:lir});
        });
        this.subscriberHubConnection.on("DeleteLegalInformationRule", (lir:LegalInformationRuleDTO) => {
            this.logger.log("Hub message: DeleteLegalInformationRule: "+ lir.id);
            this.newHubMessageReceivedSubject.next({type:"DeleteLegalInformationRule",obj:lir});
        });
        this.subscriberHubConnection.on("FolderStructureTemplate", (fst:FolderStructureTemplateDTO) => {
            this.logger.log("Hub message: FolderStructureTemplate: "+ fst.id);
            this.newHubMessageReceivedSubject.next({type:"FolderStructureTemplate",obj:fst});
        });
        this.subscriberHubConnection.on("DeleteFolderStructureTemplate", (fst:FolderStructureTemplateDTO) => {
            this.logger.log("Hub message: DeleteFolderStructureTemplate: "+ fst.id);
            this.newHubMessageReceivedSubject.next({type:"DeleteFolderStructureTemplate",obj:fst});
        });
        this.subscriberHubConnection.on("DocumentTemplate", (dt:DocumentTemplateDTO) => {
            this.logger.log("Hub message: DocumentTemplate: "+ dt.id);
            this.newHubMessageReceivedSubject.next({type:"DocumentTemplate",obj:dt});
        });
        this.subscriberHubConnection.on("DeleteDocumentTemplate", (dt:DocumentTemplateDTO) => {
            this.logger.log("Hub message: DeleteDocumentTemplate: "+ dt.id);
            this.newHubMessageReceivedSubject.next({type:"DeleteDocumentTemplate",obj:dt});
        });
        this.subscriberHubConnection.on("EmailTemplate", (et:EmailTemplateDTO) => {
            this.logger.log("Hub message: EmailTemplate: "+ et.id);
            this.newHubMessageReceivedSubject.next({type:"EmailTemplate",obj:et});
        });
        this.subscriberHubConnection.on("PrivateAuctionAuthorization", (paapd:PrivateAuctionAuthorizationPublishingData) => {
            this.logger.log("Hub message: PrivateAuctionAuthorization: "+ paapd.auctionId);
            this.newHubMessageReceivedSubject.next({type:"PrivateAuctionAuthorization",obj:paapd});
        });
        this.subscriberHubConnection.on("LockAccount", (lapd:LockAccountPublishingData) => {
            this.logger.log("Hub message: LockAccount: "+ lapd.userId);
            this.newHubMessageReceivedSubject.next({type:"LockAccount",obj:lapd});
        });
        this.subscriberHubConnection.on("Judge", (j:JudgeDTO) => {
            this.logger.log("Hub message: Judge: "+ j.id);
            this.newHubMessageReceivedSubject.next({type:"Judge",obj:j});
        });
        this.subscriberHubConnection.on("DeleteJudge", (j:JudgeDTO) => {
            this.logger.log("Hub message: DeleteJudge: "+ j.id);
            this.newHubMessageReceivedSubject.next({type:"DeleteJudge",obj:j});
        });
        this.subscriberHubConnection.on("Link", (l:LinkDTO) => {
            this.logger.log("Hub message: Link: "+ l.id);
            this.newHubMessageReceivedSubject.next({type:"Link",obj:l});
        });
        this.subscriberHubConnection.on("DeleteLink", (l:LinkDTO) => {
            this.logger.log("Hub message: DeleteLink: "+ l.id);
            this.newHubMessageReceivedSubject.next({type:"DeleteLink",obj:l});
        });
        this.subscriberHubConnection.on("Portfolio", (ppd:PortfolioPublishingData) => {
            this.logger.log("Hub message: Portfolio: "+ ppd.id);
            this.newHubMessageReceivedSubject.next({type:"Portfolio",obj:ppd});
        });
        this.subscriberHubConnection.on("DeletePortfolio", (ppd:PortfolioPublishingData) => {
            this.logger.log("Hub message: DeletePortfolio: "+ ppd.id);
            this.newHubMessageReceivedSubject.next({type:"DeletePortfolio",obj:ppd});
        });
        this.subscriberHubConnection.on("Client", (cpd:ClientPublishingData) => {
            this.logger.log("Hub message: Client: "+ cpd.clientId);
            this.newHubMessageReceivedSubject.next({type:"Client",obj:cpd});
        });
        this.subscriberHubConnection.on("DeleteClient", (cpd:ClientPublishingData) => {
            this.logger.log("Hub message: DeleteClient: "+ cpd.clientId);
            this.newHubMessageReceivedSubject.next({type:"DeleteClient",obj:cpd});
        });
        this.subscriberHubConnection.on("Vendor", (vpd:VendorPublishingData) => {
            this.logger.log("Hub message: Vendor: "+ vpd.vendorId);
            this.newHubMessageReceivedSubject.next({type:"Vendor",obj:vpd});
        });
        this.subscriberHubConnection.on("DeleteVendor", (vpd:VendorPublishingData) => {
            this.logger.log("Hub message: DeleteVendor: "+ vpd.vendorId);
            this.newHubMessageReceivedSubject.next({type:"DeleteVendor",obj:vpd});
        });
        this.subscriberHubConnection.on("LawFirm", (vpd:LawFirmPublishingData) => {
            this.logger.log("Hub message: LawFirm: "+ vpd.LawFirmId);
            this.newHubMessageReceivedSubject.next({type:"LawFirm",obj:vpd});
        });
        this.subscriberHubConnection.on("DeleteLawFirm", (vpd:LawFirmPublishingData) => {
            this.logger.log("Hub message: DeleteLawFirm: "+ vpd.LawFirmId);
            this.newHubMessageReceivedSubject.next({type:"DeleteLawFirm",obj:vpd});
        });
    }

    private async connectionStart() {
        const connection = this.subscriberHubConnection;
        if (!connection || connection.state !== HubConnectionState.Connected) {
            this.logger.log("SignalR connectionStart called but connection is not connected");
            return;
        }
        this.logger.log("SignalR connection started. ConnectionId=" + connection.connectionId);
        await this.authenticateSubscriber();
    }
  
    private connectionReconnecting(error?: Error){
        this.logger.log("SignalR connection reconnecting" + (error?(": " + error.message):""));
    }

    private async connectionReconnected() {
        const connection = this.subscriberHubConnection;
        if (!connection || connection.state !== HubConnectionState.Connected) {
            this.logger.log("SignalR reconnected callback but connection is not connected");
            return;
        }
        this.logger.log("SignalR connection reconnected. ConnectionId=" + connection.connectionId);

        await this.authenticateSubscriber();
        this.connectionReconnectedSubject.next(undefined);
    }

    private connectionClose(connection: HubConnection, error?: Error) {
        this.logger.log("SignalR connection closed" + (error ? ": " + error.message : "") + ". ConnectionId=" +
            connection.connectionId
        );
        // Ignore an old connection that has already been replaced.
        if (connection !== this.subscriberHubConnection) {
            return;
        }

        this.scheduleReconnect();
    }

    private scheduleReconnect() {
        if (this.reconnectTimer) {
            return;
        }
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = undefined;
            this.reconnect();
        }, 1000);
    }

    private async reconnect() {
        const connection = this.subscriberHubConnection;

        if (!connection ||
            connection.state === HubConnectionState.Connected ||
            connection.state === HubConnectionState.Connecting ||
            connection.state === HubConnectionState.Reconnecting) {
            return;
        }

        try {
            await connection.start();
            this.connectionStart();
        }
        catch (reason) {
            this.hubConnectionStartError(reason);
        }
    }

    private hubConnectionStartError(reason:any){
        this.logger.log(reason);
        this.scheduleReconnect();
    }
}